import TextareaInput from "../FormComponents/TextareaInput";
import SingleDateInput from "../FormComponents/SingleDateInput";
import Modal from "../common/Modal";
import { useState, useEffect, useMemo, useRef } from "react";
import { t } from "i18next";
import { get } from "lodash";
import addEditReminder from "./services/addEditReminder";
import classNames from "classnames";
import AwesomeIcon from "../AwesomeIcon";
import { faChevronLeft } from "@fortawesome/pro-regular-svg-icons";
import { useAuth } from "@qure/qure-auth";
import useSingleWorkspace from "../WorkspaceManagement/services/useSingleWorkspace";
import Dropdown from "../common/Dropdown";

export const CreateEditPatientReminder = ({
  selectedReminder,
  patient_uid,
  onCloseModal,
  refetchReminders,
}) => {
  const [reminder, setReminder] = useState(selectedReminder);
  const [isReminderSubmitInProgress, setIsReminderSubmitInProgress] = useState(false);
  const { userInfo } = useAuth();

  // Patient channel can switch `patient_uid` while this form stays mounted; freeze the uid
  // from the first render where we have a patient so save targets the correct patient.
  const patientUidWhenFormOpenedRef = useRef(null);
  if (patientUidWhenFormOpenedRef.current == null && patient_uid) {
    patientUidWhenFormOpenedRef.current = patient_uid;
  }

  // Update reminder state when selectedReminder changes
  useEffect(() => {
    setReminder(selectedReminder);
  }, [selectedReminder]);

  // Fetch workspace users for task owner dropdown
  const { data: workspaceData = {} } = useSingleWorkspace({
    isOpen: !!userInfo?.default_workspace,
    id: userInfo?.default_workspace,
  });

  // Set logged-in user as default task owner when creating a new reminder
  useEffect(() => {
    // Only set default for new reminders (no id) and when owned_by is not already set
    if (
      !selectedReminder?.id &&
      !reminder?.owned_by &&
      workspaceData?.users?.length > 0 &&
      userInfo?.details?.email
    ) {
      const loggedInUserEmail = get(userInfo, ["details", "email"], "");
      const loggedInUser = workspaceData.users.find(
        (user) =>
          user.email && user.email.toLowerCase() === loggedInUserEmail.toLowerCase()
      );

      if (loggedInUser) {
        setReminder((prev) => {
          // Only set if not already set to avoid overwriting user selection
          if (!prev?.owned_by) {
            return {
              ...prev,
              owned_by: loggedInUser.id,
            };
          }
          return prev;
        });
      }
    }
  }, [workspaceData, userInfo, selectedReminder?.id, reminder?.owned_by]);

  // Create user options for dropdown
  const taskOwnerOptions = useMemo(() => {
    const users = get(workspaceData, ["users"], []);
    const placeholderOption = { label: t("select_key") || "Select", value: null };
    const userOptions = users.map((user) => ({
      label: user.name,
      value: user.id,
    }));
    return [placeholderOption, ...userOptions];
  }, [workspaceData]);

  const onChangeContext = (val) => {
    setReminder({ ...reminder, context: val });
  };

  const onChangeDate = (val) => {
    setReminder({ ...reminder, remind_after: val });
  };

  const onChangeTaskOwner = (option) => {
    // Dropdown returns the selected option object
    const ownerId = option?.value || null;
    setReminder({ ...reminder, owned_by: ownerId });
  };

  const contextTrimmed = String(get(reminder, "context", "")).trim();
  const remindAfter = get(reminder, "remind_after", null);
  const hasDueDate = remindAfter != null && remindAfter !== "";
  const isPatientContextStale =
    patientUidWhenFormOpenedRef.current != null &&
    patient_uid != null &&
    String(patient_uid) !== String(patientUidWhenFormOpenedRef.current);
  const isSaveDisabled =
    isReminderSubmitInProgress || !contextTrimmed || !hasDueDate || isPatientContextStale;

  const onSaveReminder = () => {
    if (isSaveDisabled) return;
    const payload = {
      context: reminder.context,
      remind_after: reminder.remind_after,
      owned_by: reminder.owned_by || null,
    };
    setIsReminderSubmitInProgress(true);
    addEditReminder(payload, patient_uid, reminder?.id)
      .then((res) => {
        refetchReminders();
        onCloseModal();
      })
      .finally(() => {
        setIsReminderSubmitInProgress(false);
      });
  };

  return (
    <div className="text-left flex flex-col h-full bg-white dark:bg-gray-900 rounded-t">
      <div className="p-3 h-full grow space-y-3">
        <TextareaInput
          title={""}
          label={
            selectedReminder?.id
              ? t("label.edit_task_details")
              : t("label.add_task_details")
          }
          value={get(reminder, "context", "")}
          onChange={(e) => onChangeContext(e.target.value)}
          isClinicalField
          inputClassName="!text-left text-gray-800 dark:text-gray-200"
          placeholder={t("reminder_message_placeholder")}
          labelClassName="!text-base text-gray-700 dark:text-gray-300"
          className="!w-full"
        />
        <div className="space-y-3 !px-2 !py-3">
          <div className="text-base text-gray-700 dark:text-gray-300">
            {t("label.due_date") || "Due Date"}
          </div>
          <SingleDateInput
            tooltip={t("task_due_date") || t("scan_upload_date")}
            placeholder={t("select_date")}
            value={get(reminder, "remind_after", null)}
            onChange={(val) => onChangeDate(val)}
            inputFormat="MMM DD, YYYY"
            classNames="text-[14px]"
          />
        </div>
        <div className="space-y-3 !px-2 !py-3">
          <div className="text-base text-gray-700 dark:text-gray-300">
            {t("label.task_owner") || "Task Owner"}
          </div>
          <Dropdown
            options={taskOwnerOptions}
            selectedOption={
              taskOwnerOptions.find((opt) => opt.value === reminder?.owned_by) ||
              taskOwnerOptions[0]
            }
            onChange={onChangeTaskOwner}
            hasBorder={true}
            isBottomAligned={true}
            className="!border-gray-400 text-[14px]"
            placeholder={t("select_key") || "Select"}
          />
        </div>
      </div>
      <div className="flex justify-end mt-auto bg-gray-100 dark:bg-gray-800 px-3 py-4 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={onCloseModal}
          className="bg-gray-500 hover:bg-gray-600 dark:bg-gray-600 dark:hover:bg-gray-700 text-white px-8 py-3 rounded-md mr-4 transition-colors flex-1"
        >
          {t("button.cancel")}
        </button>
        <button
          onClick={onSaveReminder}
          disabled={isSaveDisabled}
          className={classNames(
            "bg-teal-600 hover:bg-teal-700 dark:bg-teal-600 dark:hover:bg-teal-700 text-white px-8 py-3 rounded-md transition-colors flex-1",
            {
              "!bg-gray-300 dark:!bg-gray-700 cursor-not-allowed hover:!bg-gray-300 dark:hover:!bg-gray-700":
                isSaveDisabled,
            }
          )}
        >
          {t("button.save")}
        </button>
      </div>
    </div>
  );
};

export const CreateEditPatientReminderModal = ({
  isOpen,
  onClose,
  onBack,
  patient_uid,
  selectedReminder,
  refetchReminders,
}) => {
  return (
    <Modal
      show={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          <button
            onClick={onBack ? onBack : onClose}
            className="mr-2 text-xl text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 focus:outline-none transition-colors"
            aria-label="Back"
          >
            <AwesomeIcon
              name={faChevronLeft}
              size="lg"
              className="text-gray-600 dark:text-gray-300"
            />
          </button>
          <span className="text-gray-800 dark:text-gray-100">
            {selectedReminder?.id
              ? t("edit_patient_reminder_title")
              : t("create_patient_reminder_title")}
          </span>
        </div>
      }
      titleClassName="!text-lg font-semibold"
      className="w-full md:w-10/12 lg:w-1/3 bg-gray-50 dark:bg-gray-800 flex flex-col h-full max-h-[60%] sm:max-h-[80%] overflow-hidden"
      closeOnClickAway={true}
    >
      {isOpen ? (
        <CreateEditPatientReminder
          onCloseModal={onClose}
          patient_uid={patient_uid}
          selectedReminder={selectedReminder}
          refetchReminders={refetchReminders}
        />
      ) : null}
    </Modal>
  );
};
